/**
 * Flow Optimizer — per-layer group-weight reads + normalization
 * For Media Devoted | Created By Ciro Autiero
 *
 * A formula group can score one or more layers (`appliesTo`). Each layer holds
 * its OWN weight for the group, and within a layer the applicable groups'
 * weights sum to exactly 100. These helpers read a single per-layer weight
 * defensively (tolerating the legacy flat-number shape) and normalize a layer's
 * weights to 100 via the shared largest-remainder normalizer.
 */

import type { FlowLayer, GroupWeights } from "../voluum-flow-optimizer.types";
import { normalizeWeightsToHundred } from "./normalize-weights";

/** True when any stored value is a flat number (pre per-layer migration). */
export function isLegacyGroupWeights(gw: GroupWeights): boolean {
  return Object.values(gw).some((v) => typeof v === "number");
}

/**
 * Read one group's weight for one layer. Tolerates the legacy flat-number shape
 * (returns it as-is, meaning "same weight on every layer"). Missing => 0.
 */
export function readGroupWeight(
  gw: GroupWeights,
  groupId: string,
  layer: FlowLayer,
): number {
  const entry = gw[groupId];
  if (entry == null) return 0;
  if (typeof entry === "number") return entry;
  const value = entry[layer];
  return typeof value === "number" ? value : 0;
}

/**
 * Normalize the weights of the groups applicable to `layer` so they sum to
 * exactly 100. `groupIds` must already be filtered to the groups that score
 * this layer. Returns a flat `{ groupId: integerWeight }` map for that layer.
 */
export function normalizeGroupWeightsForLayer(
  gw: GroupWeights,
  groupIds: ReadonlyArray<string>,
  layer: FlowLayer,
): Record<string, number> {
  const weightsById: Record<string, number> = {};
  for (const id of groupIds) {
    weightsById[id] = readGroupWeight(gw, id, layer);
  }
  return normalizeWeightsToHundred(weightsById, groupIds);
}
